import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { setTarget } from './familySlice'
import { sendAnalyticsEvents } from '../../analytics/common'
import { EVENTS } from '../../analytics/events'
import { fontSize, wh, tabletMinWidth } from '../../mixins'
import MainContainer from '../../UI/MainContainer'
import { selectEventsData, setEventData } from '../events/eventsSlice'
import UIButton from '../../UI/UIButton'

const isArabic = navigator.language.startsWith('ar')
const textAlign = isArabic ? 'right' : 'left'

const Title = styled.h1`
  width: 302px;
  font-weight: 600;
  ${fontSize(32)};
  line-height: 38px;
  text-align: center;
  color: #323232;
  margin: 15px 0 8px 0;
  ${({ onePage }) => onePage && 'margin-top: 64px;'}

  @media screen and (min-width: ${tabletMinWidth}px) {
    margin: 0 0 24px 0;
    ${({ onePage }) => onePage && 'margin-top: 64px;'}
  }
`

const Description = styled.p`
  width: 296px;
  margin: 0 0 15px 0;
  text-align: center;
  ${fontSize(16)};
  line-height: 22px;
  color: #5b5b5b;

  @media screen and (min-width: ${tabletMinWidth}px) {
    margin: 0 0 40px 0;
  }
`

export const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  
  ${({ onePage }) => onePage && `
    @media (min-width: ${tabletMinWidth}px) {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: flex-start;
      justify-content: space-between;
      width: 582px;
    }
  `}
`

export const StyledItem = styled.li`
  width: 100%;
  max-width: 327px;
  height: 56px;
  padding: 13px 45px 13px 16px;
  background-color: ${(props) => (props.active ? '#C5E3FF' : '#F2F2F2')};
  border-radius: 12px;
  margin-bottom: 8px;
  display: flex;
  flex-direction: ${isArabic ? 'row-reverse' : 'row'};
  justify-content: flex-start;
  align-items: center;
  color: #323232;
  ${fontSize(16)};
  line-height: 28px;
  position: relative;
  cursor: pointer;

  ${({ onePage }) => onePage && `
    @media (min-width: ${tabletMinWidth}px) {
      max-width: 100%;
    }
  `}

  &:last-child {
    margin-bottom: 27px;
  }

  &::after {
    content: '';
    display: block;
    ${wh('24px')};
    position: absolute;
    top: 50%;
    right: 16px;
    left: 16px;
    ${textAlign}: auto;
    margin-top: -12px;
    background: ${(props) =>
      props.active
        ? `url('../assets/checkmark-circle.svg') center no-repeat`
        : 'transparent'};
  }
`

const ChooseTarget = ({ history, onePage }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const eventsData = useSelector(selectEventsData)
  const [initEventSent, setInitEventStatus] = useState(false)
  const [whoTrack, setWhoTrack] = useState(null)
  const isDisabled = !!whoTrack
  const BUTTONS = ['child', 'partner', 'parent', 'pet', 'car', 'other']

  const renderItem = (i, index) => {
    return (
      <StyledItem
        onClick={() => {
          setWhoTrack(i);
          dispatch(setTarget(i))
        }}
        active={i === whoTrack}
        key={index}
        onePage={onePage}
      >
        <span>{t(i)}</span>
      </StyledItem>
    )
  }

  useEffect(() => {
    if (initEventSent || onePage) return
    sendAnalyticsEvents(EVENTS.onboardingRoleShown, eventsData)
    setInitEventStatus(true)
  }, [eventsData, initEventSent, onePage])

  const onContinueClick = () => {
    dispatch(setEventData({ id: 'TrackWho', data: whoTrack }))
    sendAnalyticsEvents(EVENTS.onboardingRoleSubmitted, {
      ...eventsData,
      TrackWho: whoTrack,
    })
    history.push('/create-place')
  }

  return (
    <MainContainer adaptiveHeight={840} customPadding={onePage ? '0' : '0 24px 100px'}>
      <Title onePage={onePage}>{t('who_will_tracked')}</Title>
      <Description>{t('tracking_description')}</Description>
      <StyledList onePage={onePage}>{BUTTONS.map(renderItem)}</StyledList>
      {!onePage && (
        <UIButton onClick={onContinueClick} disabled={!isDisabled} bottomPosition={'24px'} displayStickyBtn={!onePage}>
          {t('continue')}
        </UIButton>
      )}
    </MainContainer>
  )
}

export default ChooseTarget
